import { Badge } from "../ui/Badge.js";
import { Card } from "../ui/Card.js";
import { StanLadowania, StanPusty } from "../ui/stany.js";
import type { PorownanieDto } from "../../lib/api.js";

interface Props {
  wynik: PorownanieDto | null;
  trwa: boolean;
  blad: string | null;
}

/** KP-2: format polski, czyli przecinek dziesietny. */
function liczba(wartosc: number | null, jednostka: string): string {
  return wartosc === null ? "-" : `${wartosc.toFixed(1).replace(".", ",")} ${jednostka}`;
}

/**
 * Ten sam bajt audio przez wszystkich dostawcow, obok siebie.
 *
 * To jest bezposrednia odpowiedz na zgloszenie z 19.08.2026: "mam wrazenie, ze
 * ty mi pokazujesz GPT-Audio, a tak naprawde w tle dzieje sie cos kompletnie
 * innego". Zadnym logiem sie tego nie rozstrzyga, bo log mowi to, co
 * napisalismy, ze mowi. Rozstrzyga to natomiast ta tabela: trzy rozne
 * odpowiedzi na ten sam plik znacza, ze nie ma jednego ukrytego backendu.
 *
 * POMIAR STOI NAD TABELA, raz, i to jest istota ukladu: jest arbitrem dla
 * wszystkich wierszy, a nie kolejna kolumna do porownywania. Powtorzenie go
 * przy kazdym dostawcy sugerowaloby, ze kazdy liczy swoj wlasny - a liczy go
 * deterministyczny kod, jeden dla wszystkich.
 */
export function MicPorownanie({ wynik, trwa, blad }: Props): React.JSX.Element {
  return (
    <Card tytul="Porównanie dostawców">
      {trwa ? (
        <StanLadowania tekst="Puszczam to samo nagranie przez wszystkich dostawców..." />
      ) : blad ? (
        <StanPusty tytul="Nie udało się porównać" opis={blad} />
      ) : !wynik ? (
        <StanPusty
          tytul="Jeszcze nie porównywano"
          opis={
            "Kliknij „Porównaj dostawców” przy wyniku, żeby puścić to samo nagranie przez każdy skonfigurowany model."
          }
        />
      ) : (
        <>
          {/* Arbiter. Jedna wartość dla całej tabeli, nie kolumna. */}
          <p className="historia-lead">
            Pomiar sygnału: <strong>{liczba(wynik.pomiar.syllablesPerSecond, "sylaby na sekundę")}</strong>
            {wynik.pomiar.paceLabel ? ` (${wynik.pomiar.paceLabel})` : ""},{" "}
            <strong>{liczba(wynik.pomiar.f0RangeSemitones, "półtonu rozpiętości")}</strong>
            {wynik.pomiar.toneLabel ? ` (${wynik.pomiar.toneLabel})` : ""}. Ten sam dla każdego wiersza - to on
            rozstrzyga, a nie modele.
          </p>

          <table className="historia-tabela" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th role="columnheader" scope="col">
                  Dostawca
                </th>
                <th role="columnheader" scope="col">
                  Emocja
                </th>
                <th role="columnheader" scope="col">
                  Opis brzmienia
                </th>
                <th role="columnheader" scope="col">
                  Czas
                </th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {wynik.wyniki.map((w) => (
                <tr role="row" key={w.provider}>
                  <td role="cell" data-etykieta="Dostawca">
                    {w.label}
                    <br />
                    <span className="historia-bez-audio">{w.model ?? "-"}</span>
                    {/* Podmiana wersji przez dostawcę jest tu szczególnie ważna:
                        w tabeli porównawczej dwie różne wersje tego samego
                        modelu wyglądałyby jak jeden wiersz. */}
                    {w.modelUsed !== null && w.modelUsed !== w.modelRequested && (
                      <>
                        <br />
                        <span className="historia-bez-audio">zamówiono: {w.modelRequested}</span>
                      </>
                    )}
                  </td>
                  <td role="cell" data-etykieta="Emocja">
                    {w.ok ? (w.emotionLabel ?? "nie da się określić") : <Badge wariant="warning">brak odpowiedzi</Badge>}
                  </td>
                  <td role="cell" data-etykieta="Opis brzmienia">
                    {!w.ok ? (
                      <span className="historia-bez-audio">{w.error}</span>
                    ) : w.vocalCuesConflict ? (
                      /* Opis odrzucony, bo przeczył pomiarowi nad tabelą. Treść
                         modelu zostaje zacytowana w tym zdaniu - ukrycie jej
                         byłoby drugą nieprawdą. */
                      <span className="historia-bez-audio">{w.vocalCuesConflict}</span>
                    ) : (
                      (w.vocalCues ?? "-")
                    )}
                  </td>
                  <td role="cell" data-etykieta="Czas">
                    {w.latencyMs === null ? "-" : `${(w.latencyMs / 1000).toFixed(1).replace(".", ",")} s`}
                    {w.proby !== null && w.proby > 1 && (
                      <>
                        <br />
                        <span className="historia-bez-audio">prób: {w.proby}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Suma pliku, nie ozdobnik: to ona dowodzi, że każdy dostawca dostał
              dokładnie ten sam materiał. Bez niej tabela byłaby porównaniem
              trzech modeli na trzech nagraniach. */}
          {wynik.audioSha256 && (
            <p className="mic-stopka">
              Każdy dostawca dostał ten sam plik, sha256 {wynik.audioSha256.slice(0, 16)}. Pełne zapytania i surowe
              odpowiedzi są w paczce audytu tej próby.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
